import { useEffect, useMemo, useState } from 'react';

type InfiniteQueryPages<TPage> = {
  pages: TPage[];
};

type InfiniteQueryResult<TPage> = {
  currentData?: InfiniteQueryPages<TPage>;
  fetchNextPage: () => unknown;
  hasNextPage: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isUninitialized: boolean;
};

type UseInfiniteQueryViewStateParams<TQueryArgs, TPage, TCombinedData> = {
  queryArgs: TQueryArgs;
  queryResult: InfiniteQueryResult<TPage>;
  /** Keep this callback referentially stable (for example with useCallback) to avoid recomputing combined data on every render. */
  combinePages: (pages: TPage[]) => TCombinedData | undefined;
  isSearchPending?: boolean;
  extraLoading?: boolean;
};

export function useInfiniteQueryViewState<TQueryArgs, TPage, TCombinedData>({
  queryArgs,
  queryResult,
  combinePages,
  isSearchPending = false,
  extraLoading = false,
}: UseInfiniteQueryViewStateParams<TQueryArgs, TPage, TCombinedData>) {
  const {
    currentData,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isUninitialized,
  } = queryResult;

  const combinedData = useMemo(() => {
    if (!currentData?.pages.length) return undefined;

    return combinePages(currentData.pages);
  }, [combinePages, currentData]);

  const [settledArgs, setSettledArgs] = useState(queryArgs);
  useEffect(() => {
    if (!isFetching) {
      setSettledArgs(queryArgs);
    }
  }, [queryArgs, isFetching]);

  const isNewQuery = queryArgs !== settledArgs;
  const isQueryFirstLoad = isLoading || isUninitialized;
  const isDataLoading =
    isQueryFirstLoad ||
    isSearchPending ||
    extraLoading ||
    (isNewQuery && isFetching);

  const shouldRedirect = !isDataLoading && combinedData === undefined;

  return {
    combinedData,
    isDataLoading,
    shouldRedirect,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
